import { createContext, useContext, FC, useMemo, useState, useEffect } from 'react';
import noop from 'utils/noop';
import { HighestConversionCampaign } from '../../types';
import { useConversionInsightsQuery, useListId } from '../../hooks';
import { useInsightsBoundaryContext } from '../insightsBoundary';
import { useSegmentationQueryContext } from '../segmentationQuery';

export type ConversionInsightsState = {
  highestConvertingCampaigns: HighestConversionCampaign[];
  currentConversionEvent: string | null;
  totalCampaigns: number;
  overallConversionRate: number;
  updateConversion: (conversion: string | null) => void;
  isLoading: boolean;
};

export const defaultConversionState = {
  highestConvertingCampaigns: [],
  currentConversionEvent: null,
  totalCampaigns: 0,
  overallConversionRate: 0,
  updateConversion: noop,
  isLoading: false,
};

export const ConversionInsightsContext =
  createContext<ConversionInsightsState>(defaultConversionState);
export const useConversionInsightsContext = () => useContext(ConversionInsightsContext);

export const ConversionInsightsContextProvider: FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const listId = useListId();
  const { searchQuery } = useSegmentationQueryContext();
  const { startDate, endDate } = useInsightsBoundaryContext();
  const [eventName, updateConversion] = useState<string | null>(null);

  const conversionParams = useMemo(
    () => ({
      startDate: startDate?.toISOString(),
      endDate: endDate?.toISOString(),
      searchQuery,
      page: 1,
      pageSize: 5,
    }),
    [startDate, endDate, searchQuery],
  );

  const params = useMemo(
    () => ({
      ...conversionParams,
      eventName,
      emailListId: listId,
    }),
    [listId, eventName, conversionParams],
  );

  const {
    data: { highestConvertingCampaigns, totalCampaigns, overallConversionRate } = {},
    refetch,
    isLoading,
  } = useConversionInsightsQuery(params);

  const cachedHighestConvertingCampaigns = useMemo(
    () => highestConvertingCampaigns ?? [],
    [highestConvertingCampaigns],
  );

  // TODO clean this up when implementing behavior insights component
  useEffect(() => {
    if (params?.emailListId) {
      refetch();
    }
  }, [params, refetch]);

  const value = {
    highestConvertingCampaigns: cachedHighestConvertingCampaigns,
    totalCampaigns: totalCampaigns ?? 0,
    overallConversionRate: overallConversionRate ?? 0,
    currentConversionEvent: eventName,
    updateConversion,
    isLoading,
  };

  return (
    <ConversionInsightsContext.Provider value={value}>
      {children}
    </ConversionInsightsContext.Provider>
  );
};
